import { FileText, BookOpen, Scale, Megaphone, Briefcase, List, HelpCircle, Star, Layout, Lightbulb, Bell, BarChart2 } from 'lucide-react';

// Template type IDs - used as keys throughout the application
export const TEMPLATE_TYPES = {
  ANNOUNCEMENT: 'announcement',
  CASE: 'case',
  COMPARISON: 'comparison',
  FAQ: 'faq',
  FEATURE_PAGE: 'feature_page',
  GUIDE: 'guide',
  INSIGHT: 'insight',
  LANDING_PAGE: 'landing_page',
  LISTICLE: 'listicle',
  PRESS_RELEASE: 'press_release',
  RESEARCH: 'research',
} as const;

// Type for template IDs
export type TemplateType = typeof TEMPLATE_TYPES[keyof typeof TEMPLATE_TYPES];

// Mapping of template type IDs to display names
export const TEMPLATE_TYPE_NAMES: Record<TemplateType, string> = {
  [TEMPLATE_TYPES.ANNOUNCEMENT]: 'Announcement',
  [TEMPLATE_TYPES.CASE]: 'Case',
  [TEMPLATE_TYPES.COMPARISON]: 'Comparison',
  [TEMPLATE_TYPES.FAQ]: 'FAQ',
  [TEMPLATE_TYPES.FEATURE_PAGE]: 'Feature Page',
  [TEMPLATE_TYPES.GUIDE]: 'Guide',
  [TEMPLATE_TYPES.INSIGHT]: 'Insight',
  [TEMPLATE_TYPES.LANDING_PAGE]: 'Landing Page',
  [TEMPLATE_TYPES.LISTICLE]: 'Listicle',
  [TEMPLATE_TYPES.PRESS_RELEASE]: 'Press Release',
  [TEMPLATE_TYPES.RESEARCH]: 'Research',
};

// Template descriptions for each type
export const TEMPLATE_DESCRIPTIONS: Record<TemplateType, string> = {
  [TEMPLATE_TYPES.ANNOUNCEMENT]: 'Announcement or update',
  [TEMPLATE_TYPES.CASE]: 'Case study or example',
  [TEMPLATE_TYPES.COMPARISON]: 'Comparing different options or solutions',
  [TEMPLATE_TYPES.FAQ]: 'Frequently asked questions',
  [TEMPLATE_TYPES.FEATURE_PAGE]: 'Feature or landing page',
  [TEMPLATE_TYPES.GUIDE]: 'Step-by-step instructions or tutorials',
  [TEMPLATE_TYPES.INSIGHT]: 'Insight or analysis',
  [TEMPLATE_TYPES.LANDING_PAGE]: 'Landing or conversion page',
  [TEMPLATE_TYPES.LISTICLE]: 'Curated list of items or recommendations',
  [TEMPLATE_TYPES.PRESS_RELEASE]: 'Press release or news',
  [TEMPLATE_TYPES.RESEARCH]: 'Research or data-driven article',
};

// Mapping of template type IDs to Lucide icon components
export const TEMPLATE_TYPE_ICONS: Record<TemplateType, typeof FileText> = {
  [TEMPLATE_TYPES.ANNOUNCEMENT]: Bell,
  [TEMPLATE_TYPES.CASE]: Briefcase,
  [TEMPLATE_TYPES.COMPARISON]: Scale,
  [TEMPLATE_TYPES.FAQ]: HelpCircle,
  [TEMPLATE_TYPES.FEATURE_PAGE]: Star,
  [TEMPLATE_TYPES.GUIDE]: BookOpen,
  [TEMPLATE_TYPES.INSIGHT]: Lightbulb,
  [TEMPLATE_TYPES.LANDING_PAGE]: Layout,
  [TEMPLATE_TYPES.LISTICLE]: List,
  [TEMPLATE_TYPES.PRESS_RELEASE]: Megaphone,
  [TEMPLATE_TYPES.RESEARCH]: BarChart2,
};

// Instructions for AI content generation
export const TEMPLATE_TYPE_INSTRUCTIONS: Record<TemplateType, string> = {
  [TEMPLATE_TYPES.GUIDE]: 'Instructional, step-by-step reference. Subject matter should be a how-to or tutorial relevant to the website.',
  [TEMPLATE_TYPES.COMPARISON]: 'Adjustable comparison or side-by-side breakdown. Subject matter should compare products, services, or approaches.',
  [TEMPLATE_TYPES.PRESS_RELEASE]: 'Announcement or broadcast. Subject matter should be newsworthy or an update.',
  [TEMPLATE_TYPES.CASE]: 'Business use case, result-based. Subject matter should be a business scenario or case study.',
  [TEMPLATE_TYPES.LISTICLE]: 'List of items, tips, or tools. Subject matter should be a collection or list relevant to the audience.',
  [TEMPLATE_TYPES.FAQ]: 'Answers in question format. Subject matter should be common questions and answers.',
  [TEMPLATE_TYPES.FEATURE_PAGE]: 'Stack of capabilities or features. Subject matter should highlight features or capabilities.',
  [TEMPLATE_TYPES.LANDING_PAGE]: 'Conversion or signup-oriented. Subject matter should be a value proposition, offer, or call to action relevant for a landing page.',
  [TEMPLATE_TYPES.INSIGHT]: 'Thoughtful ideas, opinions, or trends. Subject matter should be an opinion, trend, or insight.',
  [TEMPLATE_TYPES.ANNOUNCEMENT]: 'Alerts, notifications, or news. Subject matter should be a timely update or alert.',
  [TEMPLATE_TYPES.RESEARCH]: 'Data-backed or structured research content. Subject matter should be a research topic or finding.',
}; 